/**
 * EstadoBadge — fuente unica de verdad para mostrar el estado del semaforo.
 *
 * Reemplaza:
 *   - `SemaforoBadge` (paquete UI, 4 estados, hex hardcoded)
 *   - `InteresBadge`  (apps/dashboard, 5 estados, inline styles)
 *
 * Acepta:
 *   - `EstadoSemaforo`     (5 estados canonicos)
 *   - `InteresConfirmado`  ('si' | 'pd' | 'sin_tocar') por compatibilidad con
 *                          datos crudos del Excel; se mapea via lib-types.
 *
 * Colores: viven en tokens.css como `--st-*`. Aqui solo los referenciamos.
 */
import type { EstadoSemaforo, InteresConfirmado } from '@aosenuma/lib-types';
import { LABEL_ESTADO_SEMAFORO, interesToEstadoSemaforo } from '@aosenuma/lib-types';

// Swap de colores 2026-05-11: contratada ahora pinta verde; en_negociacion
// ahora pinta lime. El bg rgba también se invierte para mantener contraste
// fg/bg de la familia (sin pasar por var() porque rgba con alpha custom).
const COLOR: Record<EstadoSemaforo, { bg: string; fg: string }> = {
  sin_prospectar: { bg: 'rgba(168,177,168,0.18)', fg: 'var(--color-sin-prospectar)' },
  en_valoracion:  { bg: 'rgba(106,140,170,0.18)', fg: 'var(--color-en-valoracion)' },
  en_negociacion: { bg: 'rgba(210,220,118,0.22)', fg: 'var(--color-en-negociacion)' },
  contratada:     { bg: 'rgba(58,156,126,0.18)',  fg: 'var(--color-contratada)' },
  inviable:       { bg: 'rgba(184,122,106,0.18)', fg: 'var(--color-inviable)' },
};

const FALLBACK = { bg: 'rgba(168,177,168,0.18)', fg: 'var(--color-sin-prospectar)' };

export function EstadoBadge({ value }: { value: EstadoSemaforo | InteresConfirmado }) {
  const k = isInteres(value) ? interesToEstadoSemaforo(value) : value;
  const c = COLOR[k] ?? FALLBACK;
  const label = LABEL_ESTADO_SEMAFORO[k] ?? String(k);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 8px',
        borderRadius: 'var(--radius-pill)',
        fontSize: 'var(--text-xs)',
        fontWeight: 500,
        background: c.bg,
        color: c.fg,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.fg }} />
      {label}
    </span>
  );
}

function isInteres(v: EstadoSemaforo | InteresConfirmado): v is InteresConfirmado {
  return v === 'si' || v === 'pd' || v === 'sin_tocar';
}

/** Alias deprecado para callers que aun importan SemaforoBadge. */
export const SemaforoBadge = ({ estado }: { estado: EstadoSemaforo | InteresConfirmado }) => (
  <EstadoBadge value={estado} />
);
